<template>
  <div class="auth-split">

    <!-- ── Painel esquerdo: carousel de funcionalidades ─────────────────────── -->
    <div class="auth-panel-left" aria-hidden="true">
      <!-- Background sutil -->
      <div class="left-bg" />

      <!-- Logo -->
      <div class="left-header">
        <div class="flex items-center gap-2.5">
          <div class="brand-icon">
            <UIcon name="i-heroicons-heart" class="w-5 h-5 text-white" />
          </div>
          <span class="text-xl font-bold text-white tracking-tight">{{ appName }}</span>
        </div>
        <p class="mt-1 text-sm text-indigo-200/70 ml-11">Sistema de gestão para clínicas de saúde</p>
      </div>

      <!-- Carousel -->
      <div class="left-carousel">
        <transition name="feature-fade" mode="out-in">
          <div :key="activeFeature" class="feature-card">
            <div class="feature-icon-wrap" :style="{ background: FEATURES[activeFeature].bgColor }">
              <UIcon :name="FEATURES[activeFeature].icon" class="w-7 h-7 text-white" />
            </div>
            <h2 class="feature-title">{{ FEATURES[activeFeature].title }}</h2>
            <p class="feature-desc">{{ FEATURES[activeFeature].description }}</p>
            <div class="feature-stat">
              <UIcon name="i-heroicons-check-circle" class="w-4 h-4 text-emerald-400 shrink-0" />
              <span>{{ FEATURES[activeFeature].stat }}</span>
            </div>
          </div>
        </transition>

        <!-- Dots -->
        <div class="dots">
          <button
            v-for="(_, i) in FEATURES"
            :key="i"
            class="dot"
            :class="{ 'dot-active': i === activeFeature }"
            @click="goTo(i)"
          />
        </div>
      </div>

      <!-- Rodapé esquerdo -->
      <div class="left-footer">
        <span class="text-xs text-indigo-300/50">Seguro · LGPD · Conformidade</span>
      </div>
    </div>

    <!-- ── Painel direito: formulário ────────────────────────────────────────── -->
    <div ref="rightPanel" class="auth-panel-right">
      <canvas ref="organicCanvas" class="organic-canvas" aria-hidden="true" />
      <slot />
    </div>

  </div>
</template>

<script setup lang="ts">
const config = useRuntimeConfig()
const appName = config.public.appName as string

// ── Organic growth canvas ─────────────────────────────────────────────────────
const organicCanvas = ref<HTMLCanvasElement | null>(null)
const rightPanel    = ref<HTMLDivElement | null>(null)

interface Pt { x: number; y: number }

interface Branch {
  pts:        Pt[]
  angle:      number
  len:        number
  maxLen:     number
  w:          number
  depth:      number
  children:   Branch[]
  childDone:  boolean
  flowerR:    number
  flowerMax:  number
}

interface Plant {
  branches: Branch[]
  opacity:  number
  fading:   boolean
  el:       Element
}

let ctx: CanvasRenderingContext2D | null = null
let rafId: number | null = null
const plants: Plant[] = []

const rnd = (a: number, b: number) => Math.random() * (b - a) + a

function mkBranch(o: Pt, angle: number, w: number, maxLen: number, depth = 0): Branch {
  return { pts: [{ ...o }], angle, len: 0, maxLen, w, depth, children: [], childDone: false, flowerR: 0, flowerMax: rnd(2.5, 5.5) }
}

function spawnPlant(el: Element): Plant {
  const er = el.getBoundingClientRect()
  const pr = rightPanel.value!.getBoundingClientRect()
  const cx = er.left + er.width  / 2 - pr.left
  const cy = er.top  + er.height / 2 - pr.top

  const n = Math.floor(rnd(4, 8))
  const branches: Branch[] = []
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd(-0.35, 0.35)
    const ox = cx + Math.cos(a) * (er.width  / 2 + 6)
    const oy = cy + Math.sin(a) * (er.height / 2 + 6)
    branches.push(mkBranch({ x: ox, y: oy }, a, rnd(0.5, 1.3), rnd(35, 85)))
  }
  return { branches, opacity: 0, fading: false, el }
}

function growBranch(b: Branch) {
  if (b.len >= b.maxLen) {
    if (b.flowerR < b.flowerMax) b.flowerR += 0.2
    b.children.forEach(growBranch)
    return
  }
  const step = 2
  b.angle += rnd(-0.10, 0.10)
  const last = b.pts[b.pts.length - 1]
  b.pts.push({ x: last.x + Math.cos(b.angle) * step, y: last.y + Math.sin(b.angle) * step })
  b.len += step

  // Máximo 2 níveis de ramificação para evitar stack overflow
  if (!b.childDone && b.depth < 2 && b.len > b.maxLen * 0.42 && b.children.length === 0) {
    const num = Math.random() < 0.65 ? 2 : 1
    for (let i = 0; i < num; i++) {
      const spread = rnd(0.4, 0.95) * (i === 0 ? 1 : -1)
      b.children.push(mkBranch({ ...b.pts[b.pts.length - 1] }, b.angle + spread, b.w * 0.6, b.maxLen * rnd(0.45, 0.72), b.depth + 1))
    }
  }
  if (!b.childDone && b.len > b.maxLen * 0.68) b.childDone = true
  b.children.forEach(growBranch)
}

function drawBranch(b: Branch, alpha: number) {
  if (!ctx || b.pts.length < 2) return

  const fadeIn = Math.min(1, b.len / 18)
  ctx.save()
  ctx.globalAlpha = alpha * fadeIn
  ctx.strokeStyle = 'rgba(99,102,241,0.38)'
  ctx.lineWidth   = b.w
  ctx.lineCap     = 'round'
  ctx.lineJoin    = 'round'

  ctx.beginPath()
  ctx.moveTo(b.pts[0].x, b.pts[0].y)
  for (let i = 1; i < b.pts.length; i++) {
    if (i < b.pts.length - 1) {
      const mx = (b.pts[i].x + b.pts[i + 1].x) / 2
      const my = (b.pts[i].y + b.pts[i + 1].y) / 2
      ctx.quadraticCurveTo(b.pts[i].x, b.pts[i].y, mx, my)
    } else {
      ctx.lineTo(b.pts[i].x, b.pts[i].y)
    }
  }
  ctx.stroke()

  // Flor na ponta
  if (b.flowerR > 0.4) {
    const tip = b.pts[b.pts.length - 1]
    const petals = 5
    for (let p = 0; p < petals; p++) {
      const pa = (p / petals) * Math.PI * 2
      ctx.beginPath()
      ctx.fillStyle = `rgba(167,139,250,${alpha * 0.55})`
      ctx.ellipse(
        tip.x + Math.cos(pa) * b.flowerR * 1.3,
        tip.y + Math.sin(pa) * b.flowerR * 1.3,
        b.flowerR * 0.9, b.flowerR * 0.45, pa, 0, Math.PI * 2,
      )
      ctx.fill()
    }
    ctx.beginPath()
    ctx.fillStyle = `rgba(224,210,255,${alpha * 0.9})`
    ctx.arc(tip.x, tip.y, b.flowerR * 0.38, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
  b.children.forEach(c => drawBranch(c, alpha))
}

function loop() {
  if (!ctx || !organicCanvas.value) return
  ctx.clearRect(0, 0, organicCanvas.value.width, organicCanvas.value.height)

  for (let i = plants.length - 1; i >= 0; i--) {
    const p = plants[i]
    if (p.fading) {
      p.opacity -= 0.028
      if (p.opacity <= 0) { plants.splice(i, 1); continue }
    } else {
      p.opacity = Math.min(1, p.opacity + 0.045)
      p.branches.forEach(growBranch)
    }
    p.branches.forEach(b => drawBranch(b, p.opacity))
  }

  rafId = plants.length > 0 ? requestAnimationFrame(loop) : null
}

function startLoop() {
  if (!rafId) rafId = requestAnimationFrame(loop)
}

const TARGETS = 'button, input, a, .clinic-option-btn'

function onOver(e: MouseEvent) {
  const target = (e.target as Element).closest(TARGETS)
  if (!target || !rightPanel.value) return
  if (plants.find(p => p.el === target && !p.fading)) return
  plants.push(spawnPlant(target))
  startLoop()
}

function onOut(e: MouseEvent) {
  const target = (e.target as Element).closest(TARGETS)
  if (!target) return
  plants.forEach(p => { if (p.el === target) p.fading = true })
}

function resize() {
  if (!organicCanvas.value || !rightPanel.value) return
  const r = rightPanel.value.getBoundingClientRect()
  organicCanvas.value.width  = r.width
  organicCanvas.value.height = r.height
}

onMounted(() => {
  if (!organicCanvas.value || !rightPanel.value) return
  ctx = organicCanvas.value.getContext('2d')
  resize()
  rightPanel.value.addEventListener('mouseover', onOver)
  rightPanel.value.addEventListener('mouseout',  onOut)
  window.addEventListener('resize', resize)
})

onUnmounted(() => {
  rightPanel.value?.removeEventListener('mouseover', onOver)
  rightPanel.value?.removeEventListener('mouseout',  onOut)
  window.removeEventListener('resize', resize)
  if (rafId) cancelAnimationFrame(rafId)
})

const FEATURES = [
  {
    icon: 'i-heroicons-calendar-days',
    bgColor: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    title: 'Agenda Completa',
    description: 'Calendário diário, semanal e mensal com drag-and-drop, agendamentos recorrentes, bloqueios de férias, teleconsulta Jitsi integrada e lembretes automáticos por e-mail.',
    stat: 'Conflitos de sala e profissional em tempo real',
  },
  {
    icon: 'i-heroicons-document-text',
    bgColor: 'linear-gradient(135deg, #10b981, #059669)',
    title: 'Prontuário Auditado',
    description: 'Anamnese estruturada, evoluções em editor rico, anexos, assinatura digital verificável publicamente e log de auditoria com retenção de 7 anos.',
    stat: 'Auditoria de acesso · 7 anos de retenção',
  },
  {
    icon: 'i-heroicons-clipboard-document-check',
    bgColor: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    title: 'Laudos e Avaliações',
    description: 'Gere laudos e relatórios clínicos em PDF, com assinatura digital verificável publicamente. Liberação diretamente para o paciente pelo portal.',
    stat: 'PDF · assinatura digital · portal do paciente',
  },
  {
    icon: 'i-heroicons-device-phone-mobile',
    bgColor: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    title: 'Portal do Paciente',
    description: 'Login por código via WhatsApp/SMS, sem senha para lembrar. O paciente confirma consultas, paga faturas com PIX, baixa laudos, assina termos e preenche o diário emocional.',
    stat: 'Login OTP · sem senha · 100% mobile',
  },
  {
    icon: 'i-heroicons-banknotes',
    bgColor: 'linear-gradient(135deg, #f59e0b, #d97706)',
    title: 'Cobranças Automáticas',
    description: 'Gere cobranças com PIX com QR Code, boleto e link de pagamento via Asaas. O paciente paga direto pelo portal. Régua de cobrança automática para inadimplentes.',
    stat: 'PIX · boleto · link · portal do paciente',
  },
  {
    icon: 'i-heroicons-arrows-right-left',
    bgColor: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    title: 'Repasse e Split',
    description: '3 modelos de comissão: percentual por consulta, aluguel fixo de sala ou sem taxa. Split automático via Asaas na origem. Extrato individual para cada profissional.',
    stat: '% por consulta · aluguel de sala · split Asaas',
  },
  {
    icon: 'i-heroicons-chart-bar',
    bgColor: 'linear-gradient(135deg, #f97316, #ea580c)',
    title: 'Relatórios Completos',
    description: 'Dashboards de faturamento, ocupação de salas, produtividade por profissional, inadimplência e relatório de repasses com gráficos — exportação para Excel, PDF e CSV.',
    stat: 'Exportação Excel · PDF · CSV',
  },
  {
    icon: 'i-heroicons-device-tablet',
    bgColor: 'linear-gradient(135deg, #14b8a6, #0d9488)',
    title: 'App Mobile Nativo',
    description: 'Aplicativo Android com login por biometria, push notifications, acesso offline (agenda, chat e prontuário em cache), câmera para anexos e splash screen personalizada.',
    stat: 'Biometria · offline · push notifications',
  },
  {
    icon: 'i-heroicons-shield-check',
    bgColor: 'linear-gradient(135deg, #ec4899, #db2777)',
    title: 'Segurança e LGPD',
    description: 'CPF, RG e telefone mascarados por padrão — revelação sob demanda com registro em auditoria. TCLE, LGPD e autorizações de menores com assinatura digital e revogação pelo paciente.',
    stat: 'Mascaramento · consentimento · LGPD art. 7 e 11',
  },
]

const activeFeature = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

function goTo(i: number) {
  activeFeature.value = i
  restartTimer()
}

function restartTimer() {
  if (timer) clearInterval(timer)
  timer = setInterval(() => {
    activeFeature.value = (activeFeature.value + 1) % FEATURES.length
  }, 6000)
}

onMounted(() => restartTimer())
onUnmounted(() => { if (timer) clearInterval(timer) })
</script>

<style scoped>
.auth-split {
  display: flex;
  min-height: 100svh;
  background: var(--bg-page);
}

/* ── Painel esquerdo ─────────────────────────────────────────────────────── */
.auth-panel-left {
  display: none;
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #1e1b4b 0%, #312e81 40%, #1d4ed8 100%);
  flex-direction: column;
  justify-content: space-between;
  padding: 2.5rem 3rem;
}

@media (min-width: 1024px) {
  .auth-panel-left {
    display: flex;
    width: 58%;
    flex-shrink: 0;
  }
}

.left-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* Círculos decorativos suaves */
.auth-panel-left::before {
  content: '';
  position: absolute;
  top: -120px;
  right: -80px;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%);
  pointer-events: none;
}
.auth-panel-left::after {
  content: '';
  position: absolute;
  bottom: -80px;
  left: -60px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(59,130,246,0.25) 0%, transparent 70%);
  pointer-events: none;
}

.left-header {
  position: relative;
  z-index: 1;
}

.brand-icon {
  width: 2.25rem;
  height: 2.25rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(99,102,241,0.5);
}

.left-carousel {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 0;
}

.feature-card {
  max-width: 440px;
}

.feature-icon-wrap {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 24px rgba(0,0,0,0.25);
}

.feature-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 0.875rem;
  letter-spacing: -0.02em;
}

.feature-desc {
  font-size: 1rem;
  line-height: 1.65;
  color: rgba(199,210,254,0.85);
  margin-bottom: 1.25rem;
}

.feature-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(167,243,208,0.9);
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 2rem;
  padding: 0.375rem 0.875rem;
  width: fit-content;
}

.dots {
  display: flex;
  gap: 0.5rem;
  margin-top: 2.5rem;
}

.dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: rgba(255,255,255,0.25);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.dot-active {
  background: #fff;
  width: 1.5rem;
  border-radius: 0.25rem;
}

.left-footer {
  position: relative;
  z-index: 1;
}

/* ── Transição do carousel ───────────────────────────────────────────────── */
.feature-fade-enter-active,
.feature-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.feature-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.feature-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Painel direito ──────────────────────────────────────────────────────── */
.auth-panel-right {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  background: var(--bg-page);
  min-height: 100svh;
  overflow: hidden;
}

.organic-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

/* Garante que o formulário fique acima do canvas */
.auth-panel-right > :not(.organic-canvas) {
  position: relative;
  z-index: 1;
}
</style>
